import qs from "qs";
import { Logger } from "../logging/logger";
import axios from "axios";
import PageConfig from "./PageConfig";
import { ColorSwatches, colorTranslation, DesignOptionKey } from "../types/DesignOptionTypes";
export const getQuantityFromUrl = (): number | undefined => {
  if (PageConfig.isSSR) return undefined;

  const quantityParameter = "qty";
  const regex = new RegExp(`[?&]${quantityParameter}(=([0-9]*)|&|#|$)`);
  const results = regex.exec(window.location.href);

  if (!results) {
    return undefined;
  }
  if (!results[2]) {
    return undefined;
  }

  return parseInt(results[2]) || undefined;
};

export const getSignedInFromUrl = (): boolean => {
  if (PageConfig.isSSR) return false;

  const signedInParameter = "signedIn";
  const regex = new RegExp(`[?&]${signedInParameter}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(window.location.href);

  if (results === null || !results[2]) {
    return false;
  }
  return results[2].toLowerCase() === "true";
};

export const getSelectionFromUrl = (): Record<string, string> | undefined => {
  if (PageConfig.isSSR) return undefined;

  const selectionParameter = "sel";
  const regex = new RegExp(`[?&]${selectionParameter}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(window.location.href);

  if (!results) {
    return undefined;
  }
  if (!results[2]) {
    return undefined;
  }

  const decodedResults = decodeURIComponent(results[2]);

  try {
    const parsedResult = JSON.parse(decodedResults);
    return parsedResult || undefined;
  } catch {
    Logger.instance.error("Failed to parse sel query param");
    return undefined;
  }
};

export const getNoPreselectedOptionsFromUrl = (): boolean => {
  if (PageConfig.isSSR) return false;

  const queryParams = getQueryParametersFromUrl();
  const paramEntry = Object.entries(queryParams).find(
    entry => entry[0].toLowerCase() === "nopreselectedoptions"
  );
  const noPreselectedOptions = paramEntry?.[1] ?? "false";
  return noPreselectedOptions.toLowerCase() === "true";
};
export const getColorsFromUrl = (locale: string): ColorSwatches | null => {
  const { colorSwatches } = getQueryParametersFromUrl();
  if (colorSwatches) {
    const colorSwatchesParsed = JSON.parse(colorSwatches);
    return {
      key: DesignOptionKey.DESIGN_COLOR,
      name: colorTranslation[locale],
      options: colorSwatchesParsed,
    };
  }
  return null;
};

export const getQueryParametersFromUrl = (): Record<string, string> => {
  try {
    const querystringParameters = qs.parse(window.location.search.substring(1));
    return removeDuplicateQuerystringParameters(querystringParameters);
  } catch {
    return {};
  }
};

export const validateUrl = async (url: string): Promise<boolean> => {
  let isValidUrl = true;

  try {
    const status = (await axios.head(url)).status;

    if (status === 404) {
      isValidUrl = false;
    }
  } catch (ex) {
    isValidUrl = false;
  }

  return isValidUrl;
};

function removeDuplicateQuerystringParameters(
  querystringParameters: qs.ParsedQs
): Record<string, string> {
  return Object.keys(querystringParameters).reduce((acc: Record<string, string>, key: string) => {
    const value = querystringParameters[key];
    if (Array.isArray(value) && typeof value[0] === "string") {
      acc[key] = value[0];
    } else if (typeof value === "string") {
      acc[key] = value;
    }
    return acc;
  }, {});
}

export function filterQueryParamsFromUrl(url: string, prefix: string): string {
  const [baseUrl, queryParamsString] = url.split("?");
  const queryParams = queryParamsString.split("&");
  const filteredParams = queryParams.filter(param => !param.startsWith(prefix));
  return `${baseUrl}?${filteredParams.join("&")}`;
}

export function splitCamelcaseStringInDifferentWords(camelCasedString: string): string {
  const splitString = camelCasedString.replace(/([a-z])([A-Z])/g, "$1 $2");
  return splitString.charAt(0).toUpperCase() + splitString.slice(1);
}
